import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Factory, Plus } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { StatusBadge } from "@/components/ui/status-badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { binderyApi, type BinderyVendor } from "@/lib/api/bindery";

export default function BinderyVendorsPage() {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState<BinderyVendor[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "inactive">("all");

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const res = await binderyApi.vendors.list({
        search: search || undefined,
        isActive: activeFilter === "all" ? undefined : activeFilter === "active",
        limit: 50,
        sortBy: "name",
        sortOrder: "asc",
      });
      setVendors(res.vendors);
      setTotal(res.total);
    } catch (err) {
      console.error("Failed to load bindery vendors", err);
    } finally {
      setLoading(false);
    }
  }, [search, activeFilter]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const columns: DataTableColumn<BinderyVendor>[] = [
    {
      id: "name",
      header: "Vendor",
      cell: (v) => (
        <button
          onClick={() => navigate(`/bindery/vendors/${v.id}`)}
          className="font-medium text-left text-primary hover:underline"
        >
          {v.name}
        </button>
      ),
    },
    {
      id: "contact",
      header: "Contact",
      cell: (v) => (
        <span className="text-sm text-slate-700">
          {v.contactPerson ?? "—"}
          {v.email && (
            <>
              <br />
              <a className="text-xs text-slate-500 hover:underline" href={`mailto:${v.email}`}>
                {v.email}
              </a>
            </>
          )}
        </span>
      ),
      hideOnMobile: true,
    },
    {
      id: "turnaround",
      header: "Turnaround",
      cell: (v) => <span className="text-sm tabular-nums">{v.defaultTurnaroundDays}d</span>,
      width: "w-24",
      align: "center",
      hideOnMobile: true,
    },
    {
      id: "requests",
      header: "Batches",
      cell: (v) => <span className="text-sm tabular-nums">{v._count?.requests ?? 0}</span>,
      width: "w-20",
      align: "center",
      hideOnMobile: true,
    },
    {
      id: "status",
      header: "Status",
      cell: (v) => (
        <StatusBadge variant={v.isActive ? "success" : "neutral"}>
          {v.isActive ? "Active" : "Inactive"}
        </StatusBadge>
      ),
      width: "w-24",
    },
  ];

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Bindery Vendors"
        description="Manage external bindery and repair vendors."
        breadcrumbItems={[{ label: "Dashboard", href: "/dashboard" }, { label: "Bindery vendors" }]}
        titleSize="md"
        searchQuery={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search vendors..."
        actions={
          <Button onClick={() => navigate("/bindery/vendors/new")}>
            <Plus className="w-4 h-4 mr-2" /> New vendor
          </Button>
        }
      />

      <div className="flex items-center gap-3">
        <Select
          value={activeFilter}
          onValueChange={(v) => setActiveFilter(v as typeof activeFilter)}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-slate-500">
          {total} vendor{total !== 1 ? "s" : ""}
        </span>
      </div>

      <DataTable
        columns={columns}
        data={vendors}
        rowKey={(v) => v.id}
        loading={loading}
        emptyState={
          <EmptyState
            icon={<Factory className="w-6 h-6 text-slate-400" />}
            title="No bindery vendors yet"
            description="Add your first bindery or repair vendor to start sending items."
            action={
              <Button onClick={() => navigate("/bindery/vendors/new")}>
                <Plus className="w-4 h-4 mr-2" /> New vendor
              </Button>
            }
          />
        }
      />
    </PageContainer>
  );
}
